import { useEffect, useRef, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Camera, Wifi, WifiOff, Crosshair, Loader2, RotateCcw, Check, Upload, AlertTriangle } from 'lucide-react';
import { useSync } from '../context/SyncContext';

const MAX_W = 1280;
const ACCURACY_LIMIT = 100; // metres — considered a precise lock

function drawStamp(ctx, w, h, lines) {
  const pad = Math.round(w * 0.02);
  const fs = Math.max(13, Math.round(w * 0.026));
  const lh = Math.round(fs * 1.35);
  const boxH = lh * lines.length + pad * 1.5;
  const grd = ctx.createLinearGradient(0, h - boxH * 1.6, 0, h);
  grd.addColorStop(0, 'rgba(2,6,23,0)');
  grd.addColorStop(1, 'rgba(2,6,23,0.82)');
  ctx.fillStyle = grd;
  ctx.fillRect(0, h - boxH * 1.6, w, boxH * 1.6);
  ctx.textBaseline = 'bottom';
  ctx.font = `600 ${fs}px system-ui, sans-serif`;
  lines.forEach((ln, i) => {
    const y = h - pad - (lines.length - 1 - i) * lh;
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillText(ln, pad + 1, y + 1);
    ctx.fillStyle = i === 0 ? '#38bdf8' : '#e2e8f0';
    ctx.fillText(ln, pad, y);
  });
}

export default function CameraCapture({ open, onClose, onCapture, title = 'Capture Proof', stampLines = [], campaignOptions = [], requireCampaign = false, results = null, initialCampaignId = '', expectedCampaignId = '', expectedClientName = '' }) {
  const { online } = useSync();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const watchRef = useRef(null);
  const [gps, setGps] = useState(null);
  const [gpsErr, setGpsErr] = useState('');
  const [camErr, setCamErr] = useState('');
  const [preview, setPreview] = useState(null);
  const [selKey, setSelKey] = useState('');
  const [result, setResult] = useState('');
  const [mismatch, setMismatch] = useState(null);

  const stopStream = useCallback(() => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setPreview(null); setCamErr(''); setGpsErr(''); setGps(null); setMismatch(null);
    const pre = initialCampaignId ? (campaignOptions.find((c) => (c.key || c.id) === initialCampaignId || c.id === initialCampaignId || c.campaign_id === initialCampaignId) || null) : null;
    setSelKey(pre ? (pre.key || pre.id) : '');
    setResult('');
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; try { await videoRef.current.play(); } catch (_) {} }
      } catch (e) {
        setCamErr('Camera not available. Use the upload fallback below.');
      }
    })();
    return () => { cancelled = true; stopStream(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stopStream]);

  useEffect(() => {
    if (!open) return undefined;
    if (!('geolocation' in navigator)) { setGpsErr('Geolocation unsupported on this device.'); return undefined; }
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => { setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: Math.round(pos.coords.accuracy) }); setGpsErr(''); },
      (err) => setGpsErr(err.message || 'Waiting for GPS lock…'),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 25000 }
    );
    return () => { if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current); };
  }, [open]);

  const gpsLocked = !!gps;
  const precise = gpsLocked && gps.accuracy <= ACCURACY_LIMIT;

  const buildLines = () => {
    const now = new Date();
    const ts = now.toLocaleString();
    const g = gps ? `GPS ${gps.lat.toFixed(5)}, ${gps.lng.toFixed(5)} · ±${gps.accuracy}m` : 'GPS —';
    return [...stampLines, g, ts];
  };

  const renderToBlob = (source, sw, sh) => {
    const scale = Math.min(1, MAX_W / sw);
    const w = Math.round(sw * scale);
    const h = Math.round(sh * scale);
    const canvas = canvasRef.current;
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(source, 0, 0, w, h);
    drawStamp(ctx, w, h, buildLines());
    canvas.toBlob((blob) => { if (blob) setPreview({ url: URL.createObjectURL(blob), blob }); }, 'image/jpeg', 0.7);
  };

  const shoot = () => {
    if (!gpsLocked) return;
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    renderToBlob(v, v.videoWidth, v.videoHeight);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file || !gpsLocked) return;
    const img = new Image();
    img.onload = () => renderToBlob(img, img.naturalWidth, img.naturalHeight);
    img.src = URL.createObjectURL(file);
  };

  const doConfirm = () => {
    const sel = campaignOptions.find((c) => (c.key || c.id) === selKey) || null;
    onCapture({ blob: preview.blob, name: `capture_${Date.now()}.jpg`, gps, campaignId: sel?.campaign_id || sel?.id || '', clientUserId: sel?.client_user_id || '', clientName: sel?.client_name || sel?.label || '', result });
    setPreview(null);
    setMismatch(null);
    stopStream();
    onClose();
  };

  const confirm = () => {
    if (!preview) return;
    const sel = campaignOptions.find((c) => (c.key || c.id) === selKey) || null;
    if (requireCampaign && !sel) return;
    if (results && !result) return;
    // Wrong-client guard: tapped client differs from this wall's recorded occupant -> warn before saving
    if (sel && expectedClientName) {
      const selCamp = sel.campaign_id || sel.id || '';
      const selName = (sel.client_name || sel.label || '').trim().toLowerCase();
      const expName = expectedClientName.trim().toLowerCase();
      const differs = expectedCampaignId
        ? (selCamp ? selCamp !== expectedCampaignId : selName !== expName)
        : selName !== expName;
      if (differs) { setMismatch({ expected: expectedClientName, selected: sel.client_name || sel.label }); return; }
    }
    doConfirm();
  };

  const canConfirm = preview && (!requireCampaign || selKey) && (!results || result);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setPreview(null); stopStream(); onClose(); } }}>
      <DialogContent className="relative flex max-h-[92dvh] w-[95vw] max-w-md flex-col gap-0 overflow-hidden border-border bg-white p-0" data-testid="camera-capture-modal">
        <DialogTitle className="shrink-0 px-4 pt-4 font-head text-lg text-slate-900">{title}</DialogTitle>
        <DialogDescription className="sr-only">Live camera capture with network and GPS overlays; photo is stamped and compressed before queueing.</DialogDescription>

        <div className="relative mt-3 w-full min-h-0 flex-1 overflow-hidden bg-black">
          {!preview ? (
            <video ref={videoRef} playsInline muted className="h-full w-full object-cover" data-testid="camera-live-video" />
          ) : (
            <img src={preview.url} alt="preview" className="h-full w-full object-cover" data-testid="camera-preview-image" />
          )}

          {/* Overlay badges */}
          <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between gap-2 p-3">
            <span data-testid="camera-network-badge" className={`flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-semibold backdrop-blur ${online ? 'border-emerald-500 bg-emerald-950/70 text-emerald-300' : 'border-amber-500 bg-amber-950/70 text-amber-300'}`}>
              {online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}{online ? 'Online' : 'Offline'}
            </span>
            <span data-testid="camera-gps-badge" className={`flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-semibold backdrop-blur ${precise ? 'border-emerald-500 bg-emerald-950/70 text-emerald-300' : gpsLocked ? 'border-sky-500 bg-sky-950/70 text-sky-300' : 'border-rose-500 bg-rose-950/70 text-rose-300'}`}>
              <Crosshair className="h-3.5 w-3.5" />{gpsLocked ? `±${gps.accuracy}m` : 'Locating…'}
            </span>
          </div>

          {camErr && !preview && (
            <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-slate-600">{camErr}</div>
          )}
        </div>

        <div className="shrink-0 space-y-2 overflow-y-auto p-4">
          {campaignOptions.length > 0 && (
            <div data-testid="camera-client-picker">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Which client / campaign is on this space?{requireCampaign && <span className="text-rose-500"> *</span>}</p>
              <div className="flex gap-1.5 overflow-x-auto pb-1">
                {campaignOptions.map((c) => { const k = c.key || c.id; return (
                  <button key={k} data-testid={`camera-client-${k}`} onClick={() => setSelKey(k)}
                    className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${selKey === k ? 'border-primary bg-primary text-primary-foreground' : 'border-slate-400 text-slate-700'}`}>
                    {c.label || c.client_name || c.name}
                  </button>
                ); })}
              </div>
              {requireCampaign && !selKey && (
                <p data-testid="camera-client-required" className="mt-1 text-[11px] text-rose-600">Tap the client whose board is on this space to continue.</p>
              )}
            </div>
          )}
          {results && (
            <div data-testid="camera-result-picker" className="flex gap-2">
              {results.map((r) => (
                <button key={r.key} data-testid={`camera-result-${r.key}`} onClick={() => setResult(r.key)}
                  className={`flex-1 rounded-lg border-2 px-3 py-2 text-sm font-semibold transition ${result === r.key ? `${r.active} text-white` : 'border-slate-400 text-slate-700'}`}>
                  {r.label}
                </button>
              ))}
            </div>
          )}
          {!gpsLocked && (
            <p data-testid="camera-gps-blocker" className="rounded-lg border border-rose-700 bg-rose-950/40 px-3 py-2 text-xs text-rose-300">
              <Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" /> {gpsErr || 'Acquiring precise GPS — capture is locked until a fix is obtained.'}
            </p>
          )}

          {!preview ? (
            <div className="flex items-center gap-2">
              <Button data-testid="camera-shutter-btn" onClick={shoot} disabled={!gpsLocked || !!camErr} className="h-12 flex-1 text-base font-semibold">
                <Camera className="mr-2 h-5 w-5" /> Capture
              </Button>
              <label data-testid="camera-upload-fallback" className={`flex h-12 cursor-pointer items-center justify-center rounded-md border border-border px-3 text-sm ${!gpsLocked ? 'pointer-events-none opacity-40' : 'text-slate-600 hover:text-slate-900'}`}>
                <Upload className="h-5 w-5" />
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} disabled={!gpsLocked} />
              </label>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button variant="secondary" data-testid="camera-retake-btn" onClick={() => setPreview(null)} className="h-12 flex-1">
                <RotateCcw className="mr-2 h-4 w-4" /> Retake
              </Button>
              <Button data-testid="camera-confirm-btn" onClick={confirm} disabled={!canConfirm} className="h-12 flex-1 bg-emerald-600 text-base font-semibold hover:bg-emerald-500">
                <Check className="mr-2 h-5 w-5" /> Use Photo
              </Button>
            </div>
          )}
        </div>
        <canvas ref={canvasRef} className="hidden" />

        {mismatch && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 p-4" data-testid="camera-mismatch-dialog">
            <div className="w-full max-w-sm space-y-3 rounded-xl border-2 border-amber-500 bg-white p-5">
              <p className="flex items-center gap-2 text-lg font-black text-slate-900"><AlertTriangle className="h-6 w-6 text-amber-500" /> गलत client?</p>
              {stampLines.length > 0 && (
                <p className="rounded-md bg-slate-100 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-600">{stampLines.join(' — ')}</p>
              )}
              <p className="text-base font-bold leading-snug text-slate-800">
                इस wall पर <span className="text-amber-700">{mismatch.expected}</span> दर्ज है — सच में <span className="text-rose-700">{mismatch.selected}</span> capture करना है?
              </p>
              <p className="text-xs text-slate-500">
                This wall has {mismatch.expected} on record. Continue only if the board really belongs to {mismatch.selected}.
              </p>
              <div className="flex gap-2 pt-1">
                <Button variant="secondary" data-testid="camera-mismatch-cancel" onClick={() => setMismatch(null)} className="h-12 flex-1">Cancel</Button>
                <Button data-testid="camera-mismatch-confirm" onClick={doConfirm} className="h-12 flex-1 bg-amber-500 font-bold text-black hover:bg-amber-400">हाँ, सच है — Continue</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
